/**
 * A raiz: decide o que se vê e liga o socket.
 *
 * Só há três ecrãs possíveis — a verificar a sessão, a autenticação, e a app —
 * por isso não há router: uma leitura do `pathname` chega para os convites, e
 * um router completo seria peso morto num cliente que vive numa só página.
 */

import { useEffect, useRef, useState } from "react";
import { AppShell } from "./components/AppShell";
import { AuthScreen } from "./components/AuthScreen";
import { InviteLanding } from "./components/InviteLanding";
import { useStore } from "./store/store";

const inviteCode = (path: string): string | null => {
  const match = /^\/invite\/([A-Za-z0-9_-]{1,32})\/?$/.exec(path);
  return match?.[1] ?? null;
};

export function App() {
  const authStatus = useStore((s) => s.authStatus);
  const connectionDetail = useStore((s) => s.connectionDetail);
  const ready = useStore((s) => s.ready);
  const bootstrap = useStore((s) => s.bootstrap);
  const connect = useStore((s) => s.connect);
  const disconnect = useStore((s) => s.disconnect);

  const [invite, setInvite] = useState(() => inviteCode(window.location.pathname));

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  // Aterrar no ESCRITÓRIO 2D é o arranque primário. O applyReady já entra logo
  // no escritório do primeiro servidor, mas isto é a rede de segurança que
  // sobrevive a qualquer timing (reconexão que preservou @me, hot-reload): na
  // PRIMEIRA vez que a app fica pronta sem servidor à frente, abre o escritório
  // do primeiro servidor. Corre uma só vez — depois o @me/DMs ficam livres.
  const bootedRef = useRef(false);
  useEffect(() => {
    if (!ready || bootedRef.current) return;
    bootedRef.current = true;
    const store = useStore.getState();
    if (store.activeServerId === null) {
      const first = Object.values(store.servers)[0];
      if (first) store.selectServer(first.id);
    }
  }, [ready]);

  // O socket só existe com sessão. `disconnect` no cleanup fecha-o no hot-reload
  // do dev — senão acumulam-se ligações a cada gravação de ficheiro.
  useEffect(() => {
    if (authStatus !== "authenticated") return;
    connect();
    return () => disconnect();
  }, [authStatus, connect, disconnect]);

  function dismissInvite() {
    setInvite(null);
    window.history.replaceState(null, "", "/");
  }

  // O convite espera pela sessão em vez de a exigir a seco.
  //
  // Estava antes do ecrã de entrada, e por isso quem abria o link sem sessão
  // via o cartão do servidor com a frase «Entre na sua conta para aceitar» e
  // nenhuma maneira de o fazer. A única saída era «Continuar sem aceitar», que
  // limpa o código E reescreve o URL — a pessoa fazia login e o convite tinha
  // desaparecido. Ficava autenticada, fora do servidor, a olhar para uma app
  // vazia. Era este o «tiveram dificuldade para entrar pelo link».
  //
  // O código vive em estado do React e o URL fica quieto: ao passar a
  // autenticada, este mesmo render devolve o cartão com o convite intacto, e o
  // botão de aceitar já lá está. Nada se perde pelo caminho.
  if (invite && authStatus === "authenticated") {
    return <InviteLanding code={invite} onDone={dismissInvite} />;
  }

  // "unknown" tanto é o meio segundo a ler o cookie como a API em baixo, e o
  // segundo caso pode durar. Sem o motivo à vista era uma marca pendurada no
  // ecrã, que se lê como "encravou" — e quem lê isso recarrega a página, ou vai
  // procurar o ecrã de entrada para escrever a password que não faz falta
  // nenhuma. O `connectionDetail` já traz a frase certa ("Sem ligação à API.").
  if (authStatus === "unknown") {
    return (
      <div className="splash">
        <span className="splash__mark">GreenVoice</span>
        {connectionDetail ? <p>{connectionDetail} A tentar de novo…</p> : null}
      </div>
    );
  }

  if (authStatus === "anonymous") {
    return <AuthScreen />;
  }

  if (!ready) {
    return (
      <div className="splash">
        <span className="splash__mark">GreenVoice</span>
        <p>Sincronizando…</p>
      </div>
    );
  }

  return <AppShell />;
}
